import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { DataProvider } from './context/DataContext';
import Sidebar from "./components/Sidebar/Sidebar";
import Category from "./components/Category/Category";
import Home from './components/Home/Home';
import Product from './components/Product/Product';
import Shop from './components/Shop';
import Customize from './components/Customize/Customize';
import Collections from './components/Collections/Collections';
import Cart from './components/Cart/Cart';
import Footer from './components/Footer/Footer';
import AdminOrders from './components/Admin/AdminOrders';
import DeletedOrders from './components/Admin/DeletedOrders';
import PromoCodeManager from './components/Admin/PromoCodeManager';
import AdminLogin from './components/Admin/AdminLogin';
import AdminContent from './components/Admin/AdminContent';
import OrderConfirmation from './components/OrderConfirmation/OrderConfirmation';
import AdminNotificationListener from './components/Admin/AdminNotificationListener';
import ProtectedRoute from './components/Admin/ProtectedRoute';
import { LoadingProvider } from './components/Loading/LoadingProvider';
import PageTransition from './components/PageTransition/PageTransition';
import InstallButton from './components/PWA/InstallButton';
import { usePWA } from './components/PWA/PWAManager';
// import NetworkStatus from './components/NetworkStatus/NetworkStatus';
import { registerServiceWorker } from './utils/serviceWorkerUtils';
import ScrollToTop from './components/ScrollToTop';
import Contact from './components/Contact/Contact';
import MobileNav from './components/MobileNav/MobileNav';
import MobileHeader from './components/MobileHeader/MobileHeader';
import { EntranceProvider, useEntrance } from './context/EntranceContext';
import { SpeedInsights } from "@vercel/speed-insights/react";
import MaintenancePage from './components/MaintenancePage/MaintenancePage';
import firebaseService from './services/firebaseService';
import './App.css';

function AppContent() {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  const { hasEntered } = useEntrance();

  // ── Maintenance Mode ──
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);

  useEffect(() => {
    const unsubscribe = firebaseService.subscribeToMaintenanceMode((result) => {
      setMaintenanceMode(result.isEnabled);
      setMaintenanceChecked(true);
    });
    return () => unsubscribe && unsubscribe();
  }, []);

  // Hide Sidebar/Header only on Home Page if not entered
  const isHome = location.pathname === '/';
  const showSidebar = !isAdminPage && (!isHome || hasEntered);

  // PWA functionality
  const { isInstalled, updateAvailable, updateApp } = usePWA();

  // Re-register Service Worker on app start
  useEffect(() => {
    const reRegisterServiceWorker = async () => {
      try {
        console.log('🔄 Re-registering Service Worker...');
        await registerServiceWorker();
        console.log('✅ Service Worker re-registered successfully');
      } catch (error) {
        console.error('❌ Failed to re-register Service Worker:', error);
      }
    };

    reRegisterServiceWorker();
  }, []);

  // Show maintenance page for non-admin visitors when enabled
  const isAdminLoggedIn = !!localStorage.getItem('adminLoggedIn');
  if (maintenanceChecked && maintenanceMode && !isAdminPage && !isAdminLoggedIn) {
    return <MaintenancePage />;
  }

  return (
    <>
      <ScrollToTop />
      <AdminNotificationListener />
      {showSidebar && <MobileHeader />}
      {showSidebar && <MobileNav />}
      <div className="main-layout-wrapper" style={{
        display: 'flex',
        width: '100%',
        minHeight: '100vh'
      }}>
        {/* Sidebar - Always visible except on Admin pages or initial Home load */}
        {showSidebar && <Sidebar />}

        {/* Main Content Area */}
        <main className="app-content" style={{
          flex: 1,
          width: '100%',
          marginLeft: showSidebar ? 'var(--sidebar-width)' : '0',
          transition: 'margin-left 0.3s ease',
          minWidth: 0,
          overflowX: 'hidden'
        }}>
          <PageTransition>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/category/:categoryId" element={<Category />} />
              <Route path="/product/:productId" element={<Product />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/customize" element={<Customize />} />
              <Route path="/collections" element={<Collections />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/order-confirmation" element={<OrderConfirmation />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin/orders" element={
                <ProtectedRoute>
                  <AdminOrders />
                </ProtectedRoute>
              } />
              <Route path="/admin/deleted-orders" element={
                <ProtectedRoute>
                  <DeletedOrders />
                </ProtectedRoute>
              } />
              <Route path="/admin/promo-codes" element={
                <ProtectedRoute>
                  <PromoCodeManager />
                </ProtectedRoute>
              } />
              <Route path="/admin/content" element={
                <ProtectedRoute>
                  <AdminContent />
                </ProtectedRoute>
              } />
              <Route path="/admin/login" element={<AdminLogin />} />
            </Routes>
            {!isAdminPage && <Footer />}

            {/* PWA Components */}
            {!isInstalled && !isAdminPage && <InstallButton />}


            {/* Update Available Banner */}
            {updateAvailable && (
              <div className="update-banner">
                <div className="update-content">
                  <span>🔄 Update Available</span>
                  <button onClick={updateApp} className="update-btn">
                    Update Now
                  </button>
                </div>
              </div>
            )}
          </PageTransition>
        </main>
      </div>
      <SpeedInsights />
    </>
  );
}

function App() {
  return (
    <DataProvider>
      <LoadingProvider>
        <EntranceProvider>
          <Router>
            <AppContent />
          </Router>
        </EntranceProvider>
      </LoadingProvider>
    </DataProvider>
  );
}

export default App;